import {
  type Observable,
  BehaviorSubject as RXBehaviorSubject,
  distinctUntilChanged,
  filter,
  take,
} from "rxjs";
import { onMounted, onUnmounted, readonly, ref } from "vue";

export type BehaviorSubjectSubscribeOptions<T> = {
  condition?: (data: T) => boolean;
  onlyChanges?: boolean;
  once?: boolean;
};
export class BehaviorSubject<T> {
  private observable: RXBehaviorSubject<T>;

  constructor(value: T) {
    this.observable = new RXBehaviorSubject<T>(value);
  }

  next(value: T) {
    this.observable.next(value);
  }

  get value() {
    return this.observable.value;
  }

  subscribe(observerOrNext: (value: T) => void, options?: BehaviorSubjectSubscribeOptions<T>) {
    let stream: RXBehaviorSubject<T> | Observable<T> = this.observable;
    if (options?.condition) {
      stream = stream.pipe(filter(options.condition));
    }
    if (options?.onlyChanges) {
      stream = stream.pipe(distinctUntilChanged());
    }
    if (options?.once) {
      stream = stream.pipe(take(1));
    }
    const subscribe = stream.subscribe(observerOrNext);
    return () => {
      if (!subscribe.closed) {
        subscribe.unsubscribe();
      }
    };
  }

  useSubscribe(observerOrNext: (value: T) => void, options?: BehaviorSubjectSubscribeOptions<T>) {
    let unsubscribe: (() => void) | undefined;

    onMounted(() => {
      unsubscribe = this.subscribe(observerOrNext, options);
    });

    onUnmounted(() => {
      unsubscribe?.();
    });
  }

  useObserve(options?: BehaviorSubjectSubscribeOptions<T>): GenericReadonlyRef<T> {
    const observableValue = ref<T>(this.value);

    this.useSubscribe((value) => {
      observableValue.value = value;
    }, options);

    return readonly(observableValue);
  }
}
